import type { ChangeEvent, JSX } from 'react';
import { createContext, useMemo, useContext } from 'react';
import type { FormGroupProps } from './FormGroup';
import { FormGroup } from './FormGroup';
import { FormContext } from '../Form/Form';

export type RadioGroupProps = FormGroupProps & {
  onChange?: ((event: ChangeEvent<HTMLInputElement>) => void) | undefined;
  value?: string | undefined;
  name: string;
};

type RadioGroupContextValues = Pick<RadioGroupProps, 'name' | 'onChange' | 'value'>;

export const RadioGroupContext = createContext<RadioGroupContextValues | null>(null);

/**
 * Connects Radio components with their parent RadioGroup; provides access to `name` and `onChange`
 */
export const useRadioGroup = () => {
  return useContext(RadioGroupContext);
};

/**
 * For grouping like form elements together. Can serve as a checkbox
 */
export const RadioGroup = ({
  children,
  name,
  onChange,
  value,
  ...props
}: RadioGroupProps): JSX.Element => {
  const formState = useContext(FormContext);
  const derivedValue = typeof formState.values[name] === 'string' ? formState.values[name] : value;
  const context = useMemo<RadioGroupContextValues>(() => {
    return {
      name,
      onChange,
      value: derivedValue,
    };
  }, [name, derivedValue, onChange]);

  return (
    <RadioGroupContext.Provider value={context}>
      <FormGroup {...props}>{children}</FormGroup>
    </RadioGroupContext.Provider>
  );
};

RadioGroup.displayName = 'RadioGroup';
